import {
    FormLayout,
    Form,
    TextField,
    Button,
    Subheading,
    Select,
    Heading,
    Stack,
    Toast,
    Frame, Checkbox
} from '@shopify/polaris';
import { useState, useCallback, useEffect } from 'react';
import Api from '../../helpers/api';
import {cloneDeep} from 'lodash';
import {CountrySelector, StateSelector} from "../Selectors";
import {filterShippingMethods, getFormError} from "../../helpers/utils";

const CustomerForm = props => {

    const [loading, setLoading] = useState(false);
    const [allShippingMethods, setAllShippingMethods] = useState({});
    const [shippingMethods, setShippingMethods] = useState([]);

    useEffect(() => {
        Api.getShippingMethods().then(results => {
            setAllShippingMethods(results.data);
        })
    }, [])

    const emptyCustomer = () => {
        return {
            'first_name': '',
            'last_name': '',
            'email': '',
            'shipping_method': null,
            'phone': '',
            'notes': '',

            location: {
                address1: '',
                address2: '',
                address3: '',
                city: '',
                zip_code: '',
                country: '',
                state: '',
            },
        }
    };

    const existingCustomer = () => {
        return {
            ...props.customer,
            ...props.customer.additional_data,
            shipping_method: props.customer?.shipping_method ? {
                name: props.customer.shipping_method?.name ?? '',
                price: props.customer.shipping_method?.price ?? '0',
                key: props.customer.shipping_method?.shopify_rate_id ?? props.customer.shipping_method?.key //fallback for old values
            } : null,
            id: props.customer.id,
        }
    }

    const [toastActive, setToastActive] = useState(false);
    const [form, setForm] = useState((props.customer && props.updating)
        ? existingCustomer()
        : emptyCustomer() );

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const address = props.updating
            ? form.default_location?.shipping
            : form.location;

        if (allShippingMethods && (allShippingMethods.data || allShippingMethods.custom))
            setShippingMethods(filterShippingMethods(address, allShippingMethods));
    }, [allShippingMethods, form]);

    const handleInputChange = useCallback((value, id) => {
        const payload = {
            [id]: value,
        };

        setForm({
            ...form,
            ...payload,
        });
    }, [form]);

    const handleAddressChange = useCallback((value, id) => {
        let keys = id.split('.');
        let clonedForm = cloneDeep(form);
        clonedForm[keys[0]][keys[1]] = value;
        setForm(clonedForm);
    }, [form]);

    const handleShippingMethodChange = useCallback((key) => {
        let clonedForm = cloneDeep(form);

        if (!key || key.length == 0) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        let method = null;
        shippingMethods.forEach((item) => {
            if (item.key == key)
                method = item;
        });

        if (!method) {
            clonedForm.shipping_method = {
                name: '',
                price: '',
                key: '',
            }
            setForm(clonedForm);
            return;
        }

        clonedForm.shipping_method = {
            name: method.name,
            price: method.price,
            key: method.key,
        }
        setForm(clonedForm);
    }, [form, shippingMethods]);

    const changeCustomShippingMethod = useCallback((value, id) => {
        let clonedForm = cloneDeep(form);
        clonedForm.shipping_method[id] = value;
        setForm(clonedForm);
    }, [form]);

    const handleSubmit = () => {
        setLoading(true);
        setErrors({});

        Api.upsertCustomer(form)
            .then (({ data }) => {
                props.onCustomerCreated(data.data);
                setLoading(false);
            })
            .catch ( error => {
                toggleToast();
                setLoading(false);

                if (error.response.status == 422) {
                    setErrors(error.response.data.errors);
                } else {
                    throw error;
                }
            });
    }

    const showError = name => getFormError(errors, name);

    const toggleToast = useCallback(() => setToastActive((toastActive) => !toastActive), []);

    const toastMarkup = toastActive ? (
      <Toast content="There has been an error processing the form." error onDismiss={toggleToast} />
    ) : null;

    return (
        <Frame>
            <Form onSubmit={handleSubmit}>
            <FormLayout>
                <FormLayout.Group>
                    <Stack vertical>
                        <Select label="Default Shipping Method"
                                options={[
                                    { label: '--Select Shipping Method--', value: '' },
                                    ...(shippingMethods.map((method) => {
                                        return {
                                            label: method.label,
                                            value: method.key + ''
                                        }
                                    }))
                                ]}
                                onChange={handleShippingMethodChange}
                                value={form.shipping_method?.key + ''}
                                id="shipping_method"
                                error={showError('shipping_method')}
                        />

                        { form.shipping_method?.key == 'custom' && (
                            <Stack distribution={"fillEvenly"}>
                                <TextField label="Shipping Method Name" id="name" value={form.shipping_method.name} onChange={changeCustomShippingMethod} />
                                <TextField label="Shipping Method Price" id="price" type={"number"} min={0} step={1.0} value={form.shipping_method.price.toString()} onChange={changeCustomShippingMethod} />
                            </Stack>
                        ) }
                    </Stack>
                </FormLayout.Group>

                <hr />
                <Subheading>Contact Information</Subheading>
                <FormLayout.Group>
                    <Stack vertical>
                        <TextField label="First Name" value={form.first_name} onChange={handleInputChange} type="text" id="first_name" error={showError('first_name')} />
                        <TextField label="Last Name" value={form.last_name} onChange={handleInputChange} type="text" id="last_name" error={showError('last_name')} />
                        <TextField label="Email" value={form.email} onChange={handleInputChange} type="email" id="email" error={showError('email')} />
                        <TextField label="Phone" value={form.phone} onChange={handleInputChange} type="tel" id="phone" error={showError('phone')} />
                    </Stack>
                </FormLayout.Group>

                {props.creating && (
                    <>
                        <hr />
                        <Heading>Location</Heading>
                        <div style={{marginLeft: '-2rem'}}>
                            <FormLayout.Group >
                                <Stack vertical>
                                    <TextField label="Company" value={form.location?.address1} onChange={handleAddressChange} type="text" id="location.address1" error={showError('location.address1')} />
                                    <TextField label="Address 1" value={form.location?.address2} onChange={handleAddressChange} type="text" id="location.address2" error={showError('location.address2')} />
                                    <TextField label="Address 2" value={form.location?.address3} onChange={handleAddressChange} type="text" id="location.address3" error={showError('location.address3')} />
                                    <TextField label="City" value={form.location?.city} onChange={handleAddressChange} type="text" id="location.city" error={showError('location.city')} />
                                    <TextField label="Postal Code" value={form.location?.zip_code} onChange={handleAddressChange} type="text" id="location.zip_code" error={showError('location.zip_code')} />
                                    <CountrySelector onChange={(val) => {handleAddressChange(val, "location.country")}}
                                                     value={form.location?.country} error={showError('location.country')} />
                                    <StateSelector onChange={(val) => {handleAddressChange(val, "location.state")}}
                                                   value={form.location?.state} error={showError('location.state')} selectedCountry={form.location?.country} />
                                </Stack>
                            </FormLayout.Group>
                        </div>
                    </>
                )}

                <hr />
                <FormLayout.Group>
                    <TextField label="Notes" value={form.notes} onChange={handleInputChange} type="text" id="notes" error={showError('notes')} multiline={4} />
                </FormLayout.Group>
                <Button loading={loading} submit primary>Submit</Button>
            </FormLayout>
            </Form>
            {toastMarkup}
        </Frame>
    );
}

export default CustomerForm;
